import { useState, useEffect, useCallback, Suspense } from 'react';
import { useDispatch } from 'react-redux';
import { Route as RRRoute, Switch as RRSwitch } from 'react-router-dom';

import { Center, Spinner, VStack } from '@frontend/components';

import DashboardLayout from 'containers/Layouts/DashboardLayout/DashboardLayout';

// import MaintenanceModePage from 'containers/MaintenanceMode/MaintenanceModePage';
import { ErrorBoundary, PaymentRequired } from 'modules/common/components';
import RedirectWrapper from 'modules/differentOrganizationPage/RedirectWrapper';
import { routes as localDigiQuoteRoutes } from 'modules/digiQuote';
import { digiQuoteRoutes } from 'modules/digiQuote/routes';
import * as modules from 'modules/index';

import { changeLocation } from 'store/app/actions';
import * as routeSagas from 'store/routeSagas';

import { HealthProvider } from 'utils/HealthContext';
import { useInterval } from 'utils/hooks';
import { CustomRoute, CustomSwitch } from 'utils/routerUtils';
import { isDigitalQuoteDomain as getIsDigitalQuoteDomain } from 'utils/siteUtils';

import { STATIC_URL } from './constants/all';
import NotFound from './containers/NotFound/NotFound';
import TeamMember from './containers/Team/TeamMember/entry';
import Team from './containers/Team/entry';
import VerifyEmail from './containers/VerifyEmail/entry';
import { isSupplier } from './permissions';

// @TODO: Migrate this app code to modules
const legacyRoutes = [
  {
    exact: true,
    path: '/team',
    component: Team,
    helpId: 'team_member_list',
    saga: routeSagas.initTeam,
    permissions: [isSupplier],
  },
  {
    exact: true,
    path: '/team-member/create',
    component: TeamMember,
    helpId: 'team_member_list',
    saga: routeSagas.initCreateTeamMember,
    permissions: [isSupplier],
  },
  {
    exact: true,
    path: '/team-member/edit/:teamMemberId',
    component: TeamMember,
    helpId: 'team_member_list',
    saga: routeSagas.initEditTeamMember,
    permissions: [isSupplier],
  },
];

const allLoggedInRoutes = [...modules.loggedInRoutes, ...legacyRoutes];

const noLayoutRoutes = [
  ...modules.loggedOutRoutes,
  ...(location.hostname === 'localhost' ? localDigiQuoteRoutes : []),
];

const SuspenseLoader = ({ children, fallback }) => (
  <ErrorBoundary showBackButton>
    <Suspense
      fallback={
        fallback ?? (
          <Center h="full">
            <Spinner size="xl" label="Loading Page" />
          </Center>
        )
      }
    >
      {children}
    </Suspense>
  </ErrorBoundary>
);

let lastAbortController;
const fetchHealthStatus = async () => {
  if (lastAbortController) {
    lastAbortController.abort(
      'Previous fetchHealthStatus time elapsed. Aborting and trying again.',
    );
  }
  const thisAbortController = (lastAbortController = new AbortController());
  const { signal } = thisAbortController;
  const done = (health) => {
    if (thisAbortController === lastAbortController) {
      lastAbortController = null;
    }
    return health;
  };
  const abort = (health, reason) => {
    thisAbortController.abort(reason);
    return done(health);
  };
  try {
    const response = await fetch(`${STATIC_URL}/health.json`, { signal });
    if (response.ok) {
      return done(await response.json());
    }
    throw new Error('Failed to fetch health status');
  } catch (e) {
    return signal.aborted
      ? //already aborted because last interval ran out. So return fact there is a delay (it's already trying again)
        done({ mode: 'unexpected_delay_checking_health' })
      : //new health is unexpected_outage_external
        abort({ mode: 'unexpected_outage_external' }, e);
  }
};

const HealthWrapper = ({ location }) => {
  const [health, setHealth] = useState({});

  const healthCheck = useCallback(async () => {
    let newHealth = await fetchHealthStatus();
    if (!newHealth || typeof newHealth.mode !== 'string') {
      newHealth = { mode: 'unexpected_health_result' };
    }
    setHealth((lastHealth) =>
      newHealth.mode !== lastHealth.mode ? newHealth : lastHealth,
    );
  }, [setHealth]);

  useInterval(healthCheck, 30_000);

  return (
    <HealthProvider
      value={{
        health: { ...health, isApiHealthy: health?.mode === 'healthy' },
        setHealth,
      }}
    >
      <RedirectWrapper>
        <Routes location={location} />
      </RedirectWrapper>
    </HealthProvider>
  );
};

const Routes = ({ location }) => {
  // const { health } = useContext(HealthContext);

  // if (!health.mode) return null;

  // if (!health.isApiHealthy && process.env.SERVER === 'production') {
  //   return (
  //     <MaintenanceModePage isDigitalQuoteDomain={isDigitalQuoteDomain()} />
  //   );
  // }

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(changeLocation({ location }));
  }, [dispatch, location]);

  const isDigitalQuoteDomain = getIsDigitalQuoteDomain();

  if (isDigitalQuoteDomain) {
    return (
      <SuspenseLoader>
        <CustomSwitch location={location}>
          {digiQuoteRoutes.map((route) => (
            <CustomRoute key={route.path} {...route} />
          ))}
          <CustomRoute component={() => <span>Not Found</span>} />
        </CustomSwitch>
      </SuspenseLoader>
    );
  }

  return (
    <RRSwitch location={location}>
      <RRRoute
        path={allLoggedInRoutes
          .map(({ path }) => path)
          .concat(['/quotes', '/orders'])
          .concat(modules.viewerRoutes.map(({ path }) => path))
          .flat(1)}
      >
        <RRSwitch location={location}>
          <RRRoute path={modules.viewerRoutes.map(({ path }) => path).flat(1)}>
            <VStack h={'100vh'}>
              <SuspenseLoader>
                <CustomSwitch location={location}>
                  {modules.viewerRoutes.map((route) => (
                    <CustomRoute key={route.path} {...route} />
                  ))}
                </CustomSwitch>
              </SuspenseLoader>
            </VStack>
          </RRRoute>
          <RRRoute>
            <DashboardLayout>
              <RRSwitch location={location}>
                <RRRoute>
                  <SuspenseLoader>
                    <CustomSwitch location={location}>
                      {allLoggedInRoutes.map((route) => (
                        <CustomRoute key={route.path} {...route} />
                      ))}
                    </CustomSwitch>
                  </SuspenseLoader>
                </RRRoute>
              </RRSwitch>
            </DashboardLayout>
          </RRRoute>
        </RRSwitch>
      </RRRoute>
      <RRRoute>
        <SuspenseLoader>
          <CustomSwitch location={location}>
            {noLayoutRoutes.map((route) => (
              <CustomRoute key={route.path} {...route} />
            ))}
            <CustomRoute exact path="/" saga={routeSagas.initRoot} />
            <CustomRoute
              exact
              path="/auth_link"
              saga={routeSagas.initAuthLink}
            />
            <CustomRoute
              exact
              path="/supplier_download"
              saga={routeSagas.initSupplierDownload}
              permissions={[isSupplier]}
            />
            <CustomRoute
              exact
              path="/verify_email"
              component={VerifyEmail}
              saga={routeSagas.initVerifyEmail}
            />
            <CustomRoute
              exact
              path="/payment-required"
              component={PaymentRequired}
            />
            <CustomRoute component={NotFound} />
          </CustomSwitch>
        </SuspenseLoader>
      </RRRoute>
    </RRSwitch>
  );
};

export default HealthWrapper;
